import React from "react";
import { twMerge } from "tailwind-merge";
import { typeRole } from "../../utils/typography";
import { ChartLabelledBy } from "../charts/ChartHeader/ChartHeader";
import { Heading } from "../Heading";
import { Skeleton } from "../Skeleton";

/**
 * Card - Enterprise surface container component
 *
 * A flexible, composable card component following enterprise design system best practices.
 * Supports modular content sections with consistent styling and accessibility.
 *
 * Usage:
 * ```tsx
 * <Card variant="outlined" size="md">
 *   <Card.Header
 *     title="Card Title"
 *     subtitle="Optional subtitle"
 *     actions={<Button variant="ghost" icon="MoreHorizontal" />}
 *   />
 *   <Card.Media src="image.jpg" alt="Description" />
 *   <Card.Content>
 *     <p>Main content goes here</p>
 *   </Card.Content>
 *   <Card.Footer>
 *     <Button variant="primary">Action</Button>
 *   </Card.Footer>
 * </Card>
 * ```
 */

// ——————————————————————————————————————————
// Types
// ——————————————————————————————————————————

export type CardVariant = "outlined" | "elevated" | "filled" | "ghost";
export interface CardProps {
  variant?: CardVariant;
  className?: string;
  children?: React.ReactNode;
  isLoading?: boolean;
  /** Enable flex column layout so children can expand to fill space */
  layout?: "default" | "flex";
}

export interface CardHeaderProps {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  /** Leading slot, rendered left of the title (e.g. a drag handle). */
  leading?: React.ReactNode;
  className?: string;
  isLoading?: boolean;
}

export interface CardMediaProps {
  src?: string;
  alt?: string;
  aspectRatio?: "square" | "video" | "wide" | "auto";
  className?: string;
  children?: React.ReactNode;
  isLoading?: boolean;
}

export interface CardContentProps {
  className?: string;
  children?: React.ReactNode;
  isLoading?: boolean;
  /** When true and parent Card has layout="flex", content expands to fill available space */
  fill?: boolean;
  /** Additional inline styles (e.g. a fixed body height). */
  style?: React.CSSProperties;
}

export interface CardFooterProps {
  className?: string;
  children?: React.ReactNode;
  align?: "start" | "end" | "center" | "between";
  isLoading?: boolean;
}

// ——————————————————————————————————————————
// Styles
// ——————————————————————————————————————————

const cardVariantStyles: Record<CardVariant, string> = {
  outlined: "bg-background-surface border border-border-default",
  elevated: "bg-background-surface border-0 shadow-md",
  filled: "bg-background-muted border-0",
  ghost: "bg-transparent border-0 shadow-none",
};

const cardBaseStyles = "rounded-lg";

const aspectRatioStyles = {
  square: "aspect-square",
  video: "aspect-video",
  wide: "aspect-[2/1]",
  auto: "",
};

const footerAlignStyles = {
  start: "justify-start",
  end: "justify-end",
  center: "justify-center",
  between: "justify-between",
};

// ——————————————————————————————————————————
// Main Card Component
// ——————————————————————————————————————————

/**
 * Does this Card carry a visible title of its own?
 *
 * `CardHeader` is a sibling of `CardContent`, not its parent, so neither can
 * answer this alone — only `Card`, which has both as children, can. Read here
 * once so a titled card declares itself, rather than every chart call site
 * inside one having to remember a wrap.
 */
function hasTitledHeader(children: React.ReactNode): boolean {
  return React.Children.toArray(children).some((child) => {
    if (!React.isValidElement(child)) return false;
    // `Children.toArray` flattens arrays but not fragments, and a header is easy
    // to end up inside one — `{cond && <><Header/><Meta/></>}`. Fragments only:
    // descending into arbitrary components would let a nested Card's own header
    // leak upward.
    if (child.type === React.Fragment) {
      return hasTitledHeader((child.props as { children?: React.ReactNode }).children);
    }
    return child.type === CardHeader && Boolean((child.props as CardHeaderProps).title);
  });
}

export function Card({ variant = "outlined", className, children, isLoading = false, layout = "default" }: CardProps) {
  // Transparent when untitled, rather than granting the title: a header-less card
  // nested in a titled `Section` would otherwise override it and re-title the
  // chart inside.
  const body = hasTitledHeader(children) ? <ChartLabelledBy>{children}</ChartLabelledBy> : children;
  return (
    <div
      className={twMerge(
        "w-full overflow-hidden",
        cardVariantStyles[variant],
        cardBaseStyles,
        layout === "flex" && "flex flex-col",
        className
      )}
      data-testid="card"
    >
      {isLoading ? (
        // Skeleton fills the card completely - respects height set on card
        <Skeleton width="100%" height="100%" className="rounded-none" />
      ) : (
        body
      )}
    </div>
  );
}

// ——————————————————————————————————————————
// Card Header
// ——————————————————————————————————————————

export function CardHeader({ title, subtitle, actions, leading, className, isLoading = false }: CardHeaderProps) {
  if (isLoading) {
    return (
      <div className={twMerge("p-6 pb-4", className)}>
        <div className="flex justify-between items-start mb-1">
          <Skeleton width={120} height={20} />
          <Skeleton width={24} height={24} />
        </div>
        {subtitle && <Skeleton width={200} height={16} />}
      </div>
    );
  }

  if (!title && !subtitle && !actions && !leading) {
    return null;
  }

  return (
    <div className={twMerge("p-6 pb-4", className)}>
      <div className="flex justify-between items-start gap-4">
        {leading && <div className="flex-shrink-0">{leading}</div>}
        <div className="min-w-0 flex-1">
          {title && (
            <div className="mb-1">
              {typeof title === "string" ? (
                <Heading tag="h3" size="sm" className="text-text-primary">
                  {title}
                </Heading>
              ) : (
                title
              )}
            </div>
          )}
          {subtitle && <div className={typeRole("bodySm", "text-text-secondary")}>{subtitle}</div>}
        </div>
        {actions && <div className="flex-shrink-0">{actions}</div>}
      </div>
    </div>
  );
}

// ——————————————————————————————————————————
// Card Media
// ——————————————————————————————————————————

export function CardMedia({ src, alt, aspectRatio = "auto", className, children, isLoading = false }: CardMediaProps) {
  if (isLoading) {
    return (
      <div className={twMerge("bg-background-muted", aspectRatioStyles[aspectRatio], className)}>
        <Skeleton width="100%" height="100%" />
      </div>
    );
  }

  if (children) {
    return <div className={twMerge("overflow-hidden", aspectRatioStyles[aspectRatio], className)}>{children}</div>;
  }

  if (!src) {
    return null;
  }

  return (
    <div className={twMerge("overflow-hidden", aspectRatioStyles[aspectRatio], className)}>
      <img src={src} alt={alt || ""} className="w-full h-full object-cover" loading="lazy" />
    </div>
  );
}

// ——————————————————————————————————————————
// Card Content
// ——————————————————————————————————————————

export function CardContent({ className, children, isLoading = false, fill = false, style }: CardContentProps) {
  // Extract padding classes from className - if any padding class is set, respect it
  const hasPadding = className?.match(/p(?:t|b|l|r|x|y)?-(?:\d+(?:\.\d+)?|px|\[.+?\])/);
  const defaultPadding = hasPadding ? "" : "px-6 py-4";

  if (isLoading) {
    return (
      <div className={twMerge(defaultPadding, fill && "flex-1 flex flex-col min-h-0", className)} style={style}>
        <div className="space-y-2">
          <Skeleton width="100%" height={16} />
          <Skeleton width="80%" height={16} />
          <Skeleton width="60%" height={16} />
        </div>
      </div>
    );
  }

  if (!children) {
    return null;
  }

  return (
    <div
      className={twMerge(defaultPadding, "text-text-primary", fill && "flex-1 flex flex-col min-h-0", className)}
      style={style}
    >
      {children}
    </div>
  );
}

// ——————————————————————————————————————————
// Card Footer
// ——————————————————————————————————————————

export function CardFooter({ className, children, align = "end", isLoading = false }: CardFooterProps) {
  if (isLoading) {
    return (
      <div className={twMerge("px-6 py-4 flex gap-2 justify-end", className)}>
        <Skeleton width={80} height={32} />
        <Skeleton width={60} height={32} />
      </div>
    );
  }

  if (!children) {
    return null;
  }

  return (
    <div className={twMerge("px-6 py-4 flex items-center gap-2", footerAlignStyles[align], className)}>{children}</div>
  );
}

// ——————————————————————————————————————————
// Compound Component API
// ——————————————————————————————————————————

Card.Header = CardHeader;
Card.Media = CardMedia;
Card.Content = CardContent;
Card.Footer = CardFooter;
